/**
 * The 14 sections of the reference sidebar (documentation/01_visual_reference/INDEX.md "Main sections"):
 * keys, icons, labels, the old permission filter (App.tsx:25425-25443, D-129 tokens) and the viewer's
 * order (D-124 `ui.sidebar_order`). Shared by the Shell (sidebar) and the settings lists card
 * (`MenuCustomizationContainer`: D-124 "every label, only permitted items, including dynamic items").
 */
import type { ReactNode } from "react";
import {
  ArrowLeftRight, ArrowUpDown, CheckCircle, Coins, DollarSign, LayoutDashboard, Percent, Settings as SettingsIcon, Shield, ShieldCheck,
  Smartphone, Store, TrendingUp, User, Wallet,
} from "lucide-react";

export type SectionKey =
  | "dashboard" | "wallets" | "dollar" | "currencies" | "movements" | "debts" | "installments" | "customer_portal"
  | "transfers_cash" | "financials" | "store" | "match" | "settings" | "audit_control" | "sub_safes";

/** The 14-item reference order (INDEX.md); `sub_safes` is appended when the office activates custody (App.tsx:25411). */
export const SECTION_ORDER: readonly SectionKey[] = [
  "dashboard", "wallets", "dollar", "currencies", "movements", "debts", "installments", "customer_portal",
  "transfers_cash", "financials", "store", "match", "settings", "audit_control",
];

/** Icon (App.tsx:25450-25575) and i18n label key per section. */
export const SECTIONS: Readonly<Record<SectionKey, { readonly icon: ReactNode; readonly labelKey: string }>> = {
  dashboard: { icon: <LayoutDashboard size={19} />, labelKey: "dashboard" },
  audit_control: { icon: <Shield size={19} className="text-amber-500" />, labelKey: "nav.audit_control" },
  sub_safes: { icon: <ShieldCheck size={19} className="text-orange-500" />, labelKey: "nav.sub_safes" },
  movements: { icon: <ArrowLeftRight size={19} />, labelKey: "movements" },
  debts: { icon: <User size={19} />, labelKey: "debts" },
  customer_portal: { icon: <Smartphone size={19} className="text-cyan-500" />, labelKey: "customer_portal" },
  installments: { icon: <Percent size={19} className="text-purple-500" />, labelKey: "nav.installments" },
  transfers_cash: { icon: <ArrowUpDown size={19} />, labelKey: "nav.transfers_cash" },
  financials: { icon: <TrendingUp size={19} />, labelKey: "nav.financials" },
  wallets: { icon: <Wallet size={19} />, labelKey: "wallets" },
  dollar: { icon: <DollarSign size={19} />, labelKey: "nav.dollar" },
  currencies: { icon: <Coins size={19} />, labelKey: "nav.currencies" },
  store: { icon: <Store size={19} />, labelKey: "store" },
  match: { icon: <CheckCircle size={19} />, labelKey: "match" },
  settings: { icon: <SettingsIcon size={19} />, labelKey: "settings" },
};

/** Permission filter of the old sidebar (App.tsx:25425-25443) over the D-129 tokens. */
export function sectionVisible(key: SectionKey, has: (t: string) => boolean, flags: { usdHidden: boolean; subSafesActive: boolean; currencyVaultHidden?: boolean; installmentsOff?: boolean; storeOff?: boolean }): boolean {
  switch (key) {
    // [C33] D-116 / D-309: an add-on module that is off hides its section (the API refuses its routes too)
    case "installments":
      return !flags.installmentsOff && has("installments");
    case "store":
      return !flags.storeOff && has("store");
    case "currencies":
      return !flags.currencyVaultHidden && has("currencies"); // old App.tsx:25420-25423 (D-069 (1): the switch hides the screen)
    case "debts":
      return has("debts") && has("view_debts_and_trusts");
    case "dollar":
      return !flags.usdHidden && has("dollar");
    case "sub_safes":
      return flags.subSafesActive && has("sub_safes");
    case "audit_control":
      return has("audit_control_section");
    default:
      return has(key);
  }
}

export interface SectionFlags {
  readonly usdHidden: boolean;
  readonly subSafesActive: boolean;
  /** [C33] `modules.installments` / `modules.store` off (D-116). */
  readonly installmentsOff?: boolean;
  readonly storeOff?: boolean;
  /** [C21] `currencies.show_vault` off (D-069 (1)). */
  readonly currencyVaultHidden?: boolean;
}

/** Reference order overlaid with the viewer's own order (D-124), `sub_safes` appended when custody is active (App.tsx:25411); unfiltered. */
export function fullSectionOrder(sidebarOrder: readonly string[] | null, flags: SectionFlags): SectionKey[] {
  const order: SectionKey[] = [];
  for (const k of sidebarOrder ?? []) if ((SECTION_ORDER as readonly string[]).includes(k) || k === "sub_safes") order.push(k as SectionKey);
  for (const k of SECTION_ORDER) if (!order.includes(k)) order.push(k);
  if (flags.subSafesActive && !order.includes("sub_safes")) order.push("sub_safes");
  return order;
}

/** `fullSectionOrder` after the permission filter (D-129). Used by the sidebar and by the sidebar-order list. */
export function orderedSections(sidebarOrder: readonly string[] | null, has: (t: string) => boolean, flags: SectionFlags): SectionKey[] {
  return fullSectionOrder(sidebarOrder, flags).filter((k) => sectionVisible(k, has, flags));
}

/**
 * App.tsx:13176-13235: a move in a filtered list swaps the two keys at their positions in the full order,
 * so hidden items keep their place. Returns the new full order to write as the `ui.*_order` preference.
 */
export function swapInOrder(full: readonly string[], a: string, b: string): string[] {
  const ia = full.indexOf(a);
  const ib = full.indexOf(b);
  if (ia === -1 || ib === -1) return [...full];
  const next = [...full];
  [next[ia], next[ib]] = [next[ib]!, next[ia]!];
  return next;
}
